import type { RankingWeights } from '@/src/types/aiRecommendation'
import type { FlightCard } from '@/src/types/flight'
import { classifyDeal } from './deal'
import { computeArrivalScore, computeArrivalTimeDistanceScore, computeDepartureScore, computeTimeDistanceScore, getArrivalSlot, getDepartureSlot } from './departure'
import { buildComparison, buildReasons } from './insights'
import { normalize } from './normalization'
import { computeAdaptiveParetoFlags } from './pareto'
import { getEffectivePrice } from './pricing'
import { checkLayoverAirportMatch, computeLayoverScore, getViaAirportCodes, sanitizeAndNormalizeWeights } from './scoring'
import type { NormalizedScores, ProcessedFlightsIndex, ProcessedFlightsResult, RankingContext } from './types'

const STRICT_MATCH_BOOST = 1.15
const PARETO_PENALTY = 0.93
const PARETO_WEIGHT_THRESHOLD = 0.22

/**
 * Layover airport boost/penalty tiers, keyed by effective priority level.
 *
 * Match boost:   multiplied onto score when flight routes via preferred airport.
 * Mismatch penalty: multiplied onto score when flight does NOT route via preferred airport.
 *
 * Combined effect at STRICT+HIGH: 1.35 / 0.75 = 1.8x separation factor,
 * which is enough to push matching flights above cheaper/faster non-matches.
 */
const LAYOVER_AIRPORT_TIERS = {
    LOW:     { matchBoost: 1.12, mismatchPenalty: 1.0 },
    MEDIUM:  { matchBoost: 1.20, mismatchPenalty: 0.92 },
    HIGH:    { matchBoost: 1.30, mismatchPenalty: 0.82 },
    STRICT_HIGH: { matchBoost: 1.35, mismatchPenalty: 0.75 }
} as const

function getLayoverAirportTier (
    flexibility: 'STRICT' | 'SOFT' | undefined,
    priorityLevel: 'HIGH' | 'MEDIUM' | 'LOW' | undefined
) {
    if (flexibility === 'STRICT' && priorityLevel === 'HIGH') return LAYOVER_AIRPORT_TIERS.STRICT_HIGH
    if (priorityLevel === 'HIGH' || flexibility === 'STRICT') return LAYOVER_AIRPORT_TIERS.HIGH
    if (priorityLevel === 'MEDIUM') return LAYOVER_AIRPORT_TIERS.MEDIUM
    return LAYOVER_AIRPORT_TIERS.LOW
}

interface Bounds {
    minPrice: number
    maxPrice: number
    minDuration: number
    maxDuration: number
    avgPrice: number
    avgDuration: number
}

function computeBounds ({
    flights,
    getPrice
}: {
    flights: FlightCard[]
    getPrice: (card: FlightCard) => number
}): Bounds {
    const prices = flights.map(getPrice)
    const durations = flights.map(f => f.totalDurationInMinutes)

    const minPrice = Math.min(...prices)
    const maxPrice = Math.max(...prices)
    const minDuration = Math.min(...durations)
    const maxDuration = Math.max(...durations)
    const avgPrice = prices.reduce((sum, p) => sum + p, 0) / prices.length
    const avgDuration = durations.reduce((sum, d) => sum + d, 0) / durations.length

    return { minPrice, maxPrice, minDuration, maxDuration, avgPrice, avgDuration }
}

// ── Pareto dimension selection based on user-relevant weights ──

function selectParetoDimensions (
    w: RankingWeights,
    hasTimePreference: boolean,
    hasArrivalTimePreference: boolean
): Array<keyof NormalizedScores> {
    const dims: Array<keyof NormalizedScores> = ['priceScore']

    const candidates: Array<{ dim: keyof NormalizedScores; weight: number }> = [
        { dim: 'durationScore', weight: w.durationWeight },
        { dim: 'layoverScore', weight: w.layoverWeight }
    ]
    if (hasTimePreference) {
        candidates.push({ dim: 'departureScore', weight: w.departureWeight })
    }
    if (hasArrivalTimePreference) {
        candidates.push({ dim: 'arrivalScore', weight: w.arrivalWeight })
    }

    for (const c of candidates) {
        if (c.weight >= PARETO_WEIGHT_THRESHOLD) {
            dims.push(c.dim)
        }
    }

    if (dims.length < 2) {
        candidates.sort((a, b) => b.weight - a.weight)
        for (const c of candidates) {
            if (!dims.includes(c.dim)) {
                dims.push(c.dim)
                break
            }
        }
    }

    return dims
}

// ── Intermediate per-flight data computed in the first pass ──

interface FlightPreScores {
    price: number
    duration: number
    stops: number
    normalized: NormalizedScores
    isExactTimeMatch: boolean
    isExactArrivalTimeMatch: boolean
    isLayoverAirportMatch: boolean
}

export function buildProcessedFlightsIndex ({
    flights,
    weights,
    context
}: {
    flights: FlightCard[]
    weights: RankingWeights
    context: RankingContext
}): ProcessedFlightsResult {
    if (!flights.length) return { smartSortIdMetrics: {}, rankedSortedFlights: [] }

    const getPrice = (card: FlightCard) =>
        getEffectivePrice({
            card,
            shouldShowMultiAirlinePrices: context.shouldShowMultiAirlinePrices,
            isPerPaxPricingEnabled: context.isPerPaxPricingEnabled
        })

    const bounds = computeBounds({ flights, getPrice })
    const hasTimePreference = !!context.departureTimePreference
    const hasArrivalTimePreference = !!context.arrivalTimePreference
    const hasLayoverAirportPreference = !!context.layoverAirportPreference?.codes?.length

    const w = sanitizeAndNormalizeWeights(weights, {
        hasDepartureTimePreference: hasTimePreference,
        hasArrivalTimePreference,
        priority: (hasTimePreference || hasArrivalTimePreference) ? context.priority : null
    })

    // ── Pass 1: compute all per-flight scores ──
    const preScores: Record<string, FlightPreScores> = {}

    for (const flight of flights) {
        const price = getPrice(flight)
        const duration = flight.totalDurationInMinutes
        const stops = flight.maxStopsInSectors

        const priceScore = normalize(price, bounds.minPrice, bounds.maxPrice, true)
        const durationScore = normalize(duration, bounds.minDuration, bounds.maxDuration, true)
        const layoverScore = computeLayoverScore(stops)

        let departureScore: number
        let isExactTimeMatch = false

        if (hasTimePreference) {
            const result = computeTimeDistanceScore(flight.firstDeparture?.time, context.departureTimePreference!)
            departureScore = result.score
            isExactTimeMatch = result.isExactMatch
        } else {
            const depSlot = getDepartureSlot(flight.firstDeparture?.time)
            departureScore = computeDepartureScore(depSlot, context.preferredDepartureSlot ?? null)
        }

        let arrivalScore: number
        let isExactArrivalTimeMatch = false

        if (hasArrivalTimePreference) {
            const result = computeArrivalTimeDistanceScore(flight.lastArrival?.time, context.arrivalTimePreference!)
            arrivalScore = result.score
            isExactArrivalTimeMatch = result.isExactMatch
        } else {
            const arrSlot = getArrivalSlot(flight.lastArrival?.time)
            arrivalScore = computeArrivalScore(arrSlot, null)
        }

        const viaAirports = getViaAirportCodes(flight, context.sectors)
        const layoverAirportMatch = checkLayoverAirportMatch(viaAirports, context.layoverAirportPreference)

        preScores[flight.id] = {
            price,
            duration,
            stops,
            normalized: { priceScore, durationScore, layoverScore, departureScore, arrivalScore },
            isExactTimeMatch,
            isExactArrivalTimeMatch,
            isLayoverAirportMatch: layoverAirportMatch.isMatch
        }
    }

    // ── Adaptive Pareto: use dimensions the user cares about ──
    const paretoDims = selectParetoDimensions(w, hasTimePreference, hasArrivalTimePreference)
    const scoreMap: Record<string, NormalizedScores> = {}
    for (const id of Object.keys(preScores)) {
        scoreMap[id] = preScores[id].normalized
    }
    const paretoFlags = computeAdaptiveParetoFlags(
        scoreMap,
        flights.map(f => f.id),
        paretoDims
    )

    // ── Pass 2: compute final aiScore with Pareto boost and multipliers ──
    const smartSortIdMetrics: ProcessedFlightsIndex = {}

    for (const flight of flights) {
        const ps = preScores[flight.id]
        const { normalized, isExactTimeMatch, isExactArrivalTimeMatch, isLayoverAirportMatch } = ps
        const isTimePreferenceMismatch =
            (hasTimePreference && !isExactTimeMatch) ||
            (hasArrivalTimePreference && !isExactArrivalTimeMatch)

        const baseScore =
            normalized.priceScore * w.priceWeight +
            normalized.durationScore * w.durationWeight +
            normalized.layoverScore * w.layoverWeight +
            normalized.departureScore * w.departureWeight +
            normalized.arrivalScore * w.arrivalWeight

        const isParetoOptimal = !!paretoFlags[flight.id]
        const paretoBoost = isParetoOptimal ? 1 : PARETO_PENALTY
        const departureStrictBoost = (hasTimePreference && isExactTimeMatch) ? STRICT_MATCH_BOOST : 1
        const arrivalStrictBoost = (hasArrivalTimePreference && isExactArrivalTimeMatch) ? STRICT_MATCH_BOOST : 1

        let layoverAirportMultiplier = 1
        if (hasLayoverAirportPreference) {
            const tier = getLayoverAirportTier(
                context.layoverAirportPreference?.flexibility,
                context.priority?.layoverAirport
            )
            layoverAirportMultiplier = isLayoverAirportMatch ? tier.matchBoost : tier.mismatchPenalty
        }

        const aiScore = baseScore * paretoBoost * departureStrictBoost * arrivalStrictBoost * layoverAirportMultiplier

        const matchScorePercent = Math.round(Math.max(0, Math.min(1, aiScore)) * 100)

        const dealLabel = classifyDeal(ps.price, bounds.avgPrice)
        const comparison = buildComparison(ps.price, ps.duration, bounds.avgPrice, bounds.avgDuration)
        const reasons = buildReasons({ normalized, isParetoOptimal, isLayoverAirportMatch, comparison })

        smartSortIdMetrics[flight.id] = {
            id: flight.id,
            basePrice: ps.price,
            baseDurationMinutes: ps.duration,
            stops: ps.stops,
            normalized,
            isParetoOptimal,
            isLayoverAirportMatch,
            isTimePreferenceMismatch,
            aiScore,
            matchScorePercent,
            dealLabel,
            comparison,
            reasons
        }
    }

    const useGating = hasLayoverAirportPreference
        && context.layoverAirportPreference?.flexibility === 'STRICT'
        && context.priority?.layoverAirport === 'HIGH'

    const scoreComparator = (a: FlightCard, b: FlightCard) => {
        const ma = smartSortIdMetrics[a.id]
        const mb = smartSortIdMetrics[b.id]
        const scoreA = ma?.aiScore ?? 0
        const scoreB = mb?.aiScore ?? 0
        if (scoreB !== scoreA) return scoreB - scoreA

        const priceA = ma?.basePrice ?? 0
        const priceB = mb?.basePrice ?? 0
        if (priceA !== priceB) return priceA - priceB

        const stopsA = ma?.stops ?? 99
        const stopsB = mb?.stops ?? 99
        if (stopsA !== stopsB) return stopsA - stopsB

        const durA = ma?.baseDurationMinutes ?? 0
        const durB = mb?.baseDurationMinutes ?? 0
        if (durA !== durB) return durA - durB

        return a.id.localeCompare(b.id)
    }

    let rankedSortedFlights: FlightCard[]

    if (useGating) {
        const matched = flights.filter(f => smartSortIdMetrics[f.id]?.isLayoverAirportMatch)
        const unmatched = flights.filter(f => !smartSortIdMetrics[f.id]?.isLayoverAirportMatch)
        matched.sort(scoreComparator)
        unmatched.sort(scoreComparator)
        rankedSortedFlights = [...matched, ...unmatched]
    } else {
        rankedSortedFlights = [...flights].sort(scoreComparator)
    }

    return { smartSortIdMetrics, rankedSortedFlights }
}
